import { type ReactNode, useEffect, useState } from 'react'
import { useSearchParams } from 'wouter'
import { FileText } from 'lucide-react'
import {
  Alert,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  EmptyState,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  FilterBar,
  eyebrowClassName,
} from '@ideon-api/ideon-ui'
import { coverageLines, latestYearWithData, quarterStates, QUARTERS, type CoverageLine as Line, type NotLicensedLine } from '../../lib/plan-entitlements'
import { usePermissionSetEntitlements } from '../../lib/use-permission-set-entitlements'
import { productLineLabel } from '../../lib/product-lines'
import { audienceLabel, CoverageLine, isQuarterlyAudience, lineKey, QuarterHeaders, type Period } from './CoverageLine'
import { CoverageHistoryDrawer } from './CoverageHistoryDrawer'
import { FeaturesCard } from './FeaturesCard'
import { BandHeader, Panel } from '../../components/Panel'

const currentQuarter = () => `Q${Math.floor(new Date().getMonth() / 3) + 1}` as Period

export function LicensingTab({ permissionSetKey }: { permissionSetKey: string }) {
  const state = usePermissionSetEntitlements(permissionSetKey)
  const [searchParams, setSearchParams] = useSearchParams()
  const [filter, setFilter] = useState('')
  // The line stays set after close so the drawer can run its close transition.
  const [history, setHistory] = useState<Line | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [notLicensedOpen, setNotLicensedOpen] = useState(false)
  // Never show another set's data while this one loads: its states could be copied by mistake.
  const data = state.status === 'ready' ? state.data : null

  const plans = data?.planEntitlements
  const years = Object.keys(plans ?? {}).sort((a, b) => Number(b) - Number(a))
  const requestedYear = searchParams.get('year') ?? ''
  const year = years.includes(requestedYear) ? requestedYear : (latestYearWithData(plans) ?? years[0] ?? String(new Date().getFullYear()))

  const update = (change: (params: URLSearchParams) => void) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        change(params)
        return params
      },
      { replace: true }
    )

  // Pin the resolved year in the URL so a copied link names the year on screen.
  const yearToPin = data && requestedYear !== year ? year : null
  useEffect(() => {
    if (yearToPin) update((params) => params.set('year', yearToPin))
  }, [yearToPin])

  if (state.status === 'error') {
    return <Alert variant="error">Failed to load: {state.message}</Alert>
  }
  if (!data) {
    return <LicensingSkeleton />
  }

  const needle = filter.trim().toLowerCase()
  const matches = (l: NotLicensedLine) =>
    !needle || [l.productLine, productLineLabel(l.productLine)].some((name) => name.toLowerCase().includes(needle))
  const all = coverageLines(plans, year)
  const licensed = all.licensed.filter(matches)
  const notLicensed = all.notLicensed.filter(matches)
  const total = licensed.length + notLicensed.length

  const openKey = searchParams.get('line')
  const requestedQuarter = searchParams.get('quarter')
  const openQuarter: Period = (QUARTERS as readonly string[]).includes(requestedQuarter ?? '')
    ? (requestedQuarter as Period)
    : currentQuarter()

  const select = (line: Line, period: Period) =>
    update((params) => {
      const key = lineKey(line.productLine, line.audience)
      const quarterly = isQuarterlyAudience(line.audience)
      const isOpen = openKey === key && (!quarterly || openQuarter === period)
      if (isOpen) {
        params.delete('line')
        params.delete('quarter')
        return
      }
      params.set('line', key)
      if (quarterly) params.set('quarter', period)
      else params.delete('quarter')
    })

  const groupedNotLicensed = notLicensed.reduce<{ productLine: string; audiences: NotLicensedLine['audience'][] }[]>((groups, l) => {
    const group = groups.find((g) => g.productLine === l.productLine)
    if (group) group.audiences.push(l.audience)
    else groups.push({ productLine: l.productLine, audiences: [l.audience] })
    return groups
  }, [])

  return (
    <div className="flex flex-col gap-6">
      <LicensingFilterBar>
        <Select value={year} onValueChange={(next) => update((params) => params.set('year', next))}>
          <SelectTrigger aria-label="Year" className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(years.includes(year) ? years : [year, ...years]).map((y) => (
              <SelectItem key={y} value={y}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SearchInput value={filter} onChange={setFilter} placeholder="Filter product line" aria-label="Filter product line" />
      </LicensingFilterBar>

      <Panel title="Coverage" subtitle={year}>
        <BandHeader label="LICENSED" count={`${licensed.length} of ${total} ${total === 1 ? 'line' : 'lines'}`} />
        {licensed.length > 0 && licensed.some((l) => isQuarterlyAudience(l.audience)) && <QuarterHeaders />}
        {licensed.length === 0 && (
          <EmptyState
            className="m-4"
            icon={<FileText className="h-10 w-10" />}
            heading={needle && total === 0 ? 'No matching lines' : 'No licensed lines'}
            description={
              !needle
                ? `No lines are licensed for ${year}.`
                : total === 0
                  ? `No product line matches "${filter.trim()}" for ${year}.`
                  : `No licensed lines match "${filter.trim()}" for ${year}.`
            }
            action={
              needle ? (
                <Button variant="text" onClick={() => setFilter('')}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        )}
        {licensed.map((line) => {
          const key = lineKey(line.productLine, line.audience)
          const quarterly = isQuarterlyAudience(line.audience)
          const open = openKey === key
          return (
            <CoverageLine
              key={key}
              productLine={line.productLine}
              audience={line.audience}
              year={year}
              periods={quarterStates(plans, year, line.productLine, line.audience)}
              openPeriod={open ? (quarterly ? openQuarter : 'Annual') : null}
              onSelect={(period) => select(line, period)}
              onHistory={() => {
                setHistory(line)
                setHistoryOpen(true)
              }}
            />
          )
        })}

        <Collapsible open={notLicensedOpen} onOpenChange={setNotLicensedOpen}>
        <BandHeader label="NOT LICENSED" count={`${notLicensed.length} ${notLicensed.length === 1 ? 'line' : 'lines'}`}>
          <CollapsibleTrigger asChild>
            <Button type="button" size="sm" variant="outline" aria-label={`${notLicensedOpen ? 'Collapse' : 'Expand'} not licensed lines`}>
              {notLicensedOpen ? 'Collapse' : 'Expand'}
            </Button>
          </CollapsibleTrigger>
        </BandHeader>
        <CollapsibleContent>
          <table className="w-full text-sm">
            <tbody>
              {groupedNotLicensed.map((g) => (
                <tr key={g.productLine} className="border-b border-ideon-gray-40 last:border-b-0">
                  <th scope="row" className="w-2/5 px-4 py-2 text-left font-medium text-ideon-gray-black">{productLineLabel(g.productLine)}</th>
                  <td className="py-2 pr-4 text-ideon-gray-70">{g.audiences.map((a) => (a ? audienceLabel(a) : 'All audiences')).join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CollapsibleContent>
        </Collapsible>
      </Panel>

      <FeaturesCard features={data.featureEntitlements ?? []} />

      {history && (
        <CoverageHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          productLine={history.productLine}
          audience={history.audience}
          permissionSetKey={permissionSetKey}
          plans={plans}
        />
      )}
    </div>
  )
}

// The filter bar renders at once, blank and disabled, so the page does not shift when data lands.
export function LicensingSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <LicensingFilterBar>
        <Select disabled>
          <SelectTrigger aria-label="Year" className="w-24">
            <SelectValue />
          </SelectTrigger>
        </Select>
        <SearchInput disabled value="" onChange={() => {}} placeholder="Filter product line" aria-label="Filter product line" />
      </LicensingFilterBar>
      <Panel title="Coverage">
        {[0, 1, 2].map((i) => (
          <div key={i} className="border-t border-ideon-gray-40 px-4 py-3">
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </Panel>
    </div>
  )
}

function LicensingFilterBar({ children: [yearSelect, search] }: { children: [ReactNode, ReactNode] }) {
  return (
    <FilterBar label="Licensing filters">
      <div className="flex shrink-0 items-center gap-3">
        <span className={`${eyebrowClassName} text-ideon-gray-70`}>YEAR</span>
        {yearSelect}
      </div>
      <div className="min-w-0 flex-1 sm:ml-auto sm:w-[260px] sm:flex-none">{search}</div>
    </FilterBar>
  )
}
